import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminConfigService } from '../../services/AdminConfigService';
import { AssistantService } from '../../services/AssistantService';
import { AssistantDefinition, ConfigViewModel, IFileListItem } from '../../client/SmartAssistantBackendClient';
import { useLoginContext } from '../login/LoginHandler';
import { useAdminConfigLoader } from '../../hooks/useAdminConfigLoader';
import { useProfileFiles } from '../../hooks/useProfileFiles';
import { useAssistantFiles } from '../../hooks/useAssistantFiles';
import { useAssistantDrafts } from '../../hooks/useAssistantDrafts';
import { useAdminWorkspace } from '../../hooks/useAdminWorkspace';
import { AdminWorkspace } from './AdminWorkspace';
import { AdminProvider } from '../../contexts/AdminContext';
import { AssistantAdminProvider } from '../../contexts/AssistantAdminContext';
import { useLoadingContext } from '../../contexts/LoadingContext';

type AppConfigurationEditorProps = {
  adminOpen: boolean;
  onClose: () => void;
};

export function AppConfigurationEditor({
  adminOpen,
  onClose,
}: AppConfigurationEditorProps) {
  const consumer = useLoginContext();
  const { beginLoadingKey } = useLoadingContext();

  const [adminConfig, setAdminConfig] = useState<ConfigViewModel | null>(null);
  const [adminConfigError, setAdminConfigError] = useState<string | null>(null);
  const [profileFiles, setProfileFiles] = useState<IFileListItem[]>([]);
  const [profileFilesError, setProfileFilesError] = useState<string | null>(null);
  const [documentAssistantDrafts, setDocumentAssistantDrafts] = useState<AssistantDefinition[]>([]);
  const [assistantSaveState, setAssistantSaveState] = useState<Record<string, { status: 'idle' | 'saving' | 'deleting' | 'error'; message: string | null }>>({});
  const [adminAssistantEditIndex, setAdminAssistantEditIndex] = useState<number | null>(null);
  const [adminAssistantFilesOpen, setAdminAssistantFilesOpen] = useState<Record<string, boolean>>({});
  const [assistantUploadState, setAssistantUploadState] = useState<Record<string, {
    file: File | null;
    status: 'idle' | 'uploading' | 'embedding' | 'done' | 'error';
    message: string | null;
  }>>({});
  const [, setAdminConfigDirty] = useState<boolean>(false);
  const adminConfigDirtyRef = useRef<boolean>(false);
  const adminConfigLoadedRef = useRef<boolean>(false);
  const adminConfigLoadPromiseRef = useRef<Promise<void> | null>(null);

  const markAdminConfigDirty = useCallback((dirty: boolean) => {
    adminConfigDirtyRef.current = dirty;
    setAdminConfigDirty(dirty);
  }, []);

  const { loadProfileFiles, handleReloadProfileFiles } = useProfileFiles({
    getUserToken: consumer.getUserToken,
    setProfileFiles,
    setProfileFilesError,
  });

  const { loadAdminConfig, handleReloadFrontendConfig } = useAdminConfigLoader({
    getUserToken: consumer.getUserToken,
    adminConfigLoadedRef,
    adminConfigLoadPromiseRef,
    adminConfigDirtyRef,
    setAdminConfig,
    setAdminConfigError,
    setDocumentAssistantDrafts,
    markAdminConfigDirty,
    loadProfileFiles,
  });

  useEffect(() => {
    if (!adminOpen) return;
    if (adminConfigLoadedRef.current) return;
    const token = consumer.getUserToken?.();
    void loadAdminConfig(token as string);
  }, [adminOpen, consumer, loadAdminConfig]);

  const openAssistantModal = useCallback((index: number) => {
    setAdminAssistantEditIndex(index);
  }, []);

  const closeAssistantModal = useCallback(() => {
    setAdminAssistantEditIndex(null);
  }, []);

  const addAssistantDraft = useCallback(() => {
    setDocumentAssistantDrafts((prev) => {
      const next = [
        ...prev,
        new AssistantDefinition({
          name: '',
          internalName: '',
          topics: '',
          vectorStoreId: '',
          isDefault: prev.length === 0,
          assistentPrompt: '',
          assistentLlmModel: '',
          promptDefinitions: [],
        }),
      ];
      setAdminAssistantEditIndex(next.length - 1);
      return next;
    });
    markAdminConfigDirty(true);
  }, [markAdminConfigDirty]);

  const existingAssistantInternalNames = useMemo(() => {
    const names = new Set<string>();
    (adminConfig?.documentAssistants ?? []).forEach((assistant) => {
      const name = assistant.internalName?.trim();
      if (name) {
        names.add(name);
      }
    });
    return names;
  }, [adminConfig?.documentAssistants]);

  const { updateAssistantSaveState } = useAssistantDrafts({
    setAssistantSaveState,
  });

  const updateAssistantUploadState = useCallback((assistantInternalName: string, patch: {
    file?: File | null;
    status?: 'idle' | 'uploading' | 'embedding' | 'done' | 'error';
    message?: string | null;
  }) => {
    setAssistantUploadState((prev) => {
      const current = prev[assistantInternalName] ?? { file: null, status: 'idle', message: null };
      return {
        ...prev,
        [assistantInternalName]: {
          ...current,
          ...patch,
        },
      };
    });
  }, []);

  const {
    listFilesForAssistant,
    embedFileForAssistant,
    deleteFileForAssistant,
    renameFolderForAssistant,
    deleteFolderForAssistant,
    uploadFilesToFolderForAssistant,
  } = useAssistantFiles({ getUserToken: consumer.getUserToken });

  const handleSaveAssistantDraft = useCallback(async (assistant: AssistantDefinition, index?: number | null) => {
    if (!assistant) return;
    const assistantKey = assistant.id ?? (index !== null && index !== undefined ? `draft-${index}` : `draft-${Date.now()}`);

    const stopLoading = beginLoadingKey('assistantSave');
    updateAssistantSaveState(assistantKey, { status: 'saving', message: null });

    try {
      const token = await consumer.getUserToken();
      await AssistantService.saveAssistant(token as string, assistant);

      await loadAdminConfig(token as string, { force: true });
      updateAssistantSaveState(assistantKey, { status: 'idle', message: null });
      markAdminConfigDirty(false);
      stopLoading();
    } catch (err: any) {
      console.error('Assistenz speichern fehlgeschlagen', err);
      updateAssistantSaveState(assistantKey, { status: 'error', message: err?.message ?? 'Unbekannter Fehler' });
      stopLoading();
    }
  }, [beginLoadingKey, consumer, loadAdminConfig, markAdminConfigDirty, updateAssistantSaveState]);

  const handleDeleteAssistantDraft = useCallback(async (index: number) => {
    const assistant = documentAssistantDrafts[index];
    if (!assistant) return;

    if (typeof window !== 'undefined') {
      const label = assistant.name?.trim() || assistant.internalName?.trim() || `Assistent ${index + 1}`;
      const confirmed = window.confirm(`Assistent wirklich löschen (${label})?`);
      if (!confirmed) return;
    }

    if (!assistant.id) {
      setDocumentAssistantDrafts((prev) => prev.filter((_, idx) => idx !== index));
      markAdminConfigDirty(true);
      return;
    }

    const assistantKey = assistant.id ?? `draft-${index}`;
    const stopLoading = beginLoadingKey('assistantDelete');
    updateAssistantSaveState(assistantKey, { status: 'deleting', message: null });

    try {
      const token = await consumer.getUserToken();
      await AssistantService.deleteAssistant(token as string, assistant);
      await loadAdminConfig(token as string, { force: true });
      updateAssistantSaveState(assistantKey, { status: 'idle', message: null });
      markAdminConfigDirty(false);
      stopLoading();
    } catch (err: any) {
      console.error('Assistenz loeschen fehlgeschlagen', err);
      updateAssistantSaveState(assistantKey, { status: 'error', message: err?.message ?? 'Unbekannter Fehler' });
      stopLoading();
    }
  }, [beginLoadingKey, consumer, documentAssistantDrafts, loadAdminConfig, markAdminConfigDirty, updateAssistantSaveState]);

  const handleSaveFrontendConfig = useCallback(async (payload: {
    openAiKeyDraft: string;
    profileFileDraft: string;
    systemPromptDraft: string;
    profileSystemPromptDraft: string;
  }) => {
    const token = await consumer?.getUserToken?.();

    const stopLoading = beginLoadingKey('adminConfigSave');
    setAdminConfigError(null);

    try {
      const trimmedKey = payload.openAiKeyDraft.trim();
      if (trimmedKey) {
        await AdminConfigService.updateApiKey(token as string, trimmedKey);
      }

      const normalize = (value: string) => value.trim().replace(/\\/g, '/');
      const trimmedProfile = normalize(payload.profileFileDraft);
      const existingProfile = normalize(adminConfig?.profileFile ?? '');
      const trimmedSystemPrompt = (payload.systemPromptDraft ?? '').trim();
      const existingSystemPrompt = (adminConfig?.systemPrompt ?? '').trim();
      const trimmedProfileSystemPrompt = (payload.profileSystemPromptDraft ?? '').trim();
      const existingProfileSystemPrompt = (adminConfig?.profileSystemPrompt ?? '').trim();
      if (trimmedProfile !== existingProfile
        || trimmedSystemPrompt !== existingSystemPrompt
        || trimmedProfileSystemPrompt !== existingProfileSystemPrompt) {
        await AdminConfigService.updateFrontendConfig(token as string, {
          profileFile: trimmedProfile || undefined,
          systemPrompt: trimmedSystemPrompt,
          profileSystemPrompt: trimmedProfileSystemPrompt,
        });
      }

      await loadAdminConfig(token as string, { force: true });
      stopLoading();
    } catch (err: any) {
      console.error('UpdateFrontendConfig fehlgeschlagen', err);
      setAdminConfigError(err?.message ?? 'Unbekannter Fehler');
      stopLoading();
    }
  }, [adminConfig?.profileFile, adminConfig?.profileSystemPrompt, adminConfig?.systemPrompt, beginLoadingKey, consumer, loadAdminConfig]);

  const adminModel = useMemo(() => ({
    config: {
      value: adminConfig,
      error: adminConfigError,
      profileFilesError,
      profileFiles,
      handleReloadProfileFiles,
      onSave: handleSaveFrontendConfig,
      onReload: handleReloadFrontendConfig,
    },
    assistants: {
      documentAssistantDrafts,
      assistantSaveState,
      adminAssistantFilesOpen,
      setAdminAssistantFilesOpen,
      addAssistantDraft,
      openAssistantModal,
      handleDeleteAssistantDraft,
      listFilesForAssistant,
      embedFileForAssistant,
      deleteFileForAssistant,
      renameFolderForAssistant,
      deleteFolderForAssistant,
      uploadFilesToFolderForAssistant,
    },
  }), [
    addAssistantDraft,
    adminAssistantFilesOpen,
    adminConfig,
    adminConfigError,
    assistantSaveState,
    openAssistantModal,
    setAdminAssistantFilesOpen,
    deleteFileForAssistant,
    deleteFolderForAssistant,
    documentAssistantDrafts,
    embedFileForAssistant,
    handleDeleteAssistantDraft,
    handleReloadFrontendConfig,
    handleReloadProfileFiles,
    handleSaveFrontendConfig,
    listFilesForAssistant,
    profileFiles,
    profileFilesError,
    renameFolderForAssistant,
    uploadFilesToFolderForAssistant,
  ]);

  const assistantAdminModel = useMemo(() => ({
    dialog: {
      open: adminAssistantEditIndex !== null,
      adminAssistantEditIndex,
      onClose: closeAssistantModal,
    },
    state: {
      documentAssistantDrafts,
      existingAssistantInternalNames,
      assistantSaveState,
      assistantUploadState,
    },
    actions: {
      saveAssistant: handleSaveAssistantDraft,
      deleteAssistant: handleDeleteAssistantDraft,
    },
    files: {
      listFilesForAssistant,
      embedFileForAssistant,
      deleteFileForAssistant,
      renameFolderForAssistant,
      deleteFolderForAssistant,
      uploadFilesToFolderForAssistant,
    },
    uploads: {
      updateAssistantUploadState,
    },
    consumer,
  }), [
    adminAssistantEditIndex,
    assistantSaveState,
    assistantUploadState,
    closeAssistantModal,
    consumer,
    deleteFileForAssistant,
    deleteFolderForAssistant,
    documentAssistantDrafts,
    embedFileForAssistant,
    existingAssistantInternalNames,
    handleDeleteAssistantDraft,
    handleSaveAssistantDraft,
    listFilesForAssistant,
    renameFolderForAssistant,
    updateAssistantUploadState,
    uploadFilesToFolderForAssistant,
  ]);

  const adminContextValue = useMemo(() => ({
    consumer,
    adminConfigState: adminModel.config,
    assistantState: adminModel.assistants,
  }), [adminModel, consumer]);

  const adminWorkspace = useAdminWorkspace({
    adminOpen,
    onClose: useCallback(() => {
      closeAssistantModal();
      onClose();
    }, [closeAssistantModal, onClose]),
    assistantEditDialog: assistantAdminModel.dialog,
  });

  return (
    <AssistantAdminProvider value={assistantAdminModel}>
      <AdminProvider value={adminContextValue}>
        <AdminWorkspace {...adminWorkspace} />
      </AdminProvider>
    </AssistantAdminProvider>
  );
}
